/**
 * GLAMRDiP header interactions
 * - Desktop mega menu: hover + click + keyboard (Esc closes), focus trapping
 * - Mobile drawer: open/close, backdrop click, Esc closes, body scroll lock
 * - Cart count: live-updates from body events (product-added-to-cart, cart:updated)
 *   via /cart.js poll
 * - Predictive search: re-dispatches a custom event the search-panel section listens for
 *
 * ≤ 4KB gzipped target. No dependencies.
 */
(() => {
  const header = document.querySelector('[data-gd-header]');
  if (!header) return;

  // ========== Mega menu ==========
  const megaBtn = header.querySelector('[data-gd-mega-toggle]');
  const megaPanel = document.getElementById('gd-mega-panel');
  let megaCloseTimer = null;

  const openMega = () => {
    if (!megaPanel) return;
    megaPanel.hidden = false;
    megaBtn?.setAttribute('aria-expanded', 'true');
    clearTimeout(megaCloseTimer);
  };
  const closeMega = () => {
    if (!megaPanel) return;
    megaPanel.hidden = true;
    megaBtn?.setAttribute('aria-expanded', 'false');
  };
  const closeMegaDelayed = () => {
    clearTimeout(megaCloseTimer);
    megaCloseTimer = setTimeout(closeMega, 150);
  };

  if (megaBtn && megaPanel) {
    // Hover behaviour (desktop)
    const megaHoverZone = megaBtn.closest('.gd-header__item--shop');
    megaHoverZone?.addEventListener('mouseenter', openMega);
    megaHoverZone?.addEventListener('mouseleave', closeMegaDelayed);
    megaPanel.addEventListener('mouseenter', () => { clearTimeout(megaCloseTimer); });
    megaPanel.addEventListener('mouseleave', closeMegaDelayed);

    // Click toggle (keyboard / touch)
    megaBtn.addEventListener('click', (e) => {
      e.preventDefault();
      if (megaPanel.hidden) openMega(); else closeMega();
    });

    // Esc to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !megaPanel.hidden) {
        closeMega();
        megaBtn.focus();
      }
    });

    // Click outside to close
    document.addEventListener('click', (e) => {
      if (megaPanel.hidden) return;
      if (!megaPanel.contains(e.target) && !megaBtn.contains(e.target)) closeMega();
    });
  }

  // ========== Kits megamenu ==========
  // Self-contained component lives in assets/gd-megamenu.js. This file
  // intentionally does NOT bind kits state — Joel 2026-04-28 rebuild.

  // ========== Mobile drawer ==========
  const mobileDrawer = document.querySelector('[data-gd-mobile-drawer]');
  const mobileOpenBtn = header.querySelector('[data-gd-mobile-open]');
  const mobileCloseBtns = document.querySelectorAll('[data-gd-mobile-close]');

  const openMobile = () => {
    if (!mobileDrawer || !mobileDrawer.hidden) return; // idempotent — already open
    mobileDrawer.hidden = false;
    // Shared reference-counted scroll lock — keeps body locked while
    // any overlay is open, restores scroll on the final close.
    if (window.gdScrollLock) window.gdScrollLock.lock(mobileDrawer);
    else document.body.style.overflow = 'hidden';
    const firstFocusable = mobileDrawer.querySelector('a[href], button');
    firstFocusable?.focus();
  };
  const closeMobile = () => {
    if (!mobileDrawer || mobileDrawer.hidden) return; // idempotent
    mobileDrawer.hidden = true;
    if (window.gdScrollLock) window.gdScrollLock.unlock(mobileDrawer);
    else document.body.style.overflow = '';
    mobileOpenBtn?.focus();
  };

  mobileOpenBtn?.addEventListener('click', openMobile);
  mobileCloseBtns.forEach(btn => btn.addEventListener('click', closeMobile));

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && mobileDrawer && !mobileDrawer.hidden) closeMobile();
  });

  // ========== Cart count (user-perceived) ==========
  // Bundles count as 1 not N — skip line items tagged _addon (kit colour
  // children) so the badge matches what the user thinks they added.
  // Mirror of the Liquid logic in sections/header.liquid.
  const cartCountEls = document.querySelectorAll('[data-gd-cart-count]');
  const effectiveCartCount = (cart) => {
    if (!cart || !Array.isArray(cart.items)) return 0;
    return cart.items.reduce((sum, item) => {
      const isAddon = item && item.properties && item.properties._addon === 'true';
      return isAddon ? sum : sum + (item.quantity || 0);
    }, 0);
  };
  const updateCartCount = (count) => {
    cartCountEls.forEach(el => {
      el.textContent = String(count);
      if (count > 0) el.removeAttribute('hidden'); else el.setAttribute('hidden', '');
    });
  };
  const refreshCart = () => fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
    .then(r => r.ok ? r.json() : null)
    .then(cart => { if (cart) updateCartCount(effectiveCartCount(cart)); })
    .catch(() => {});

  ['cart:updated', 'product-added-to-cart', 'cart-should-be-updated'].forEach(evt =>
    document.body.addEventListener(evt, refreshCart));

  // ========== Search trigger dispatch ==========
  document.querySelectorAll('[data-gd-search-open]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      document.body.dispatchEvent(new CustomEvent('gd:search-open'));
    });
  });

  // ========== Cart trigger dispatch (cart drawer picks this up) ==========
  document.querySelectorAll('[data-gd-cart-toggle]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      // Only intercept if cart drawer exists; otherwise let the anchor navigate to /cart
      const drawer = document.querySelector('[data-gd-cart-drawer]');
      if (drawer) {
        e.preventDefault();
        document.body.dispatchEvent(new CustomEvent('gd:cart-open'));
      }
    });
  });

  // ========== Scroll-frost (Joel 2026-04-28) ==========
  // Adds .is-scrolled to .gd-header once scrollY > 40 so the CSS
  // can fade in the frosted-glass treatment. rAF-throttled to coalesce
  // multiple scroll events per frame; passive listener so it never
  // blocks scroll on mobile.
  const SCROLL_FROST_THRESHOLD = 40;
  let _scrollFrostTicking = false;
  function _updateScrollFrost() {
    const scrolled = (window.scrollY || window.pageYOffset || 0) > SCROLL_FROST_THRESHOLD;
    header.classList.toggle('is-scrolled', scrolled);
    _scrollFrostTicking = false;
  }
  window.addEventListener('scroll', () => {
    if (_scrollFrostTicking) return;
    _scrollFrostTicking = true;
    window.requestAnimationFrame(_updateScrollFrost);
  }, { passive: true });
  // Initial state — handles deep-link / refresh-mid-scroll cases.
  _updateScrollFrost();
})();
